/**
 * PerformanceIndicator - Shows performance metrics for view configurations
 *
 * Helps users understand the complexity and performance implications
 * of their view configuration before saving.
 */

import React from 'react';
import { AlertTriangle, Zap, Activity, TrendingUp, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

export interface PerformanceIndicatorProps {
  chainDepth: number;        // How deep the view dependency chain is
  joinCount: number;         // Number of JOINs in the view
  estimatedRowCount?: number; // Estimated rows if available
  hasViewJoins: boolean;     // Whether any JOINs are to views (vs datasets)
  showCompact?: boolean;     // Whether to show compact version
}

export type ComplexityLevel = 'low' | 'medium' | 'high' | 'very-high';

/**
 * Calculate complexity level based on performance metrics
 */
function calculateComplexity(props: PerformanceIndicatorProps): ComplexityLevel {
  const { chainDepth, joinCount, hasViewJoins } = props;

  // Very High: Deep chains (>5) or many complex view JOINs
  if (chainDepth > 5 || (hasViewJoins && joinCount > 4)) {
    return 'very-high';
  }

  // High: Moderate depth (4-5) or multiple view JOINs (3-4)
  if (chainDepth >= 4 || (hasViewJoins && joinCount >= 3)) {
    return 'high';
  }

  // Medium: Some depth (2-3) or a few view JOINs (1-2)
  if (chainDepth >= 2 || (hasViewJoins && joinCount >= 1)) {
    return 'medium';
  }

  // Low: Shallow chain (0-1) and no view JOINs
  return 'low';
}

/**
 * Get color scheme for complexity level
 */
function getComplexityColors(level: ComplexityLevel): {
  badge: 'default' | 'secondary' | 'destructive' | 'outline';
  progress: string;
  text: string;
  bg: string;
} {
  switch (level) {
    case 'low':
      return {
        badge: 'secondary',
        progress: 'bg-success',
        text: 'text-success',
        bg: 'bg-success/10',
      };
    case 'medium':
      return {
        badge: 'outline',
        progress: 'bg-warning',
        text: 'text-warning',
        bg: 'bg-warning/10',
      };
    case 'high':
      return {
        badge: 'default',
        progress: 'bg-alert',
        text: 'text-alert',
        bg: 'bg-alert/10',
      };
    case 'very-high':
      return {
        badge: 'destructive',
        progress: 'bg-destructive',
        text: 'text-destructive',
        bg: 'bg-destructive/10',
      };
  }
}

/**
 * Get complexity percentage for progress bar (0-100)
 */
function getComplexityPercentage(level: ComplexityLevel): number {
  switch (level) {
    case 'low':
      return 25;
    case 'medium':
      return 50;
    case 'high':
      return 75;
    case 'very-high':
      return 100;
  }
}

/**
 * Get warning message based on complexity level
 */
function getWarningMessage(level: ComplexityLevel, hasViewJoins: boolean): string {
  switch (level) {
    case 'low':
      return 'Simple view configuration. Expected to perform well.';
    case 'medium':
      return hasViewJoins
        ? 'View-to-view JOINs detected. Query performance should be acceptable for most use cases.'
        : 'Moderate complexity. Should perform well for typical datasets.';
    case 'high':
      return 'Complex view chain detected. Consider materializing intermediate views for better performance.';
    case 'very-high':
      return 'Very deep dependency chain. Strongly recommend materializing this view or breaking it into smaller views.';
  }
}

/**
 * Get icon based on complexity level
 */
function getComplexityIcon(level: ComplexityLevel) {
  switch (level) {
    case 'low':
      return <Zap className="h-4 w-4" />;
    case 'medium':
      return <Activity className="h-4 w-4" />;
    case 'high':
      return <TrendingUp className="h-4 w-4" />;
    case 'very-high':
      return <AlertTriangle className="h-4 w-4" />;
  }
}

/**
 * PerformanceIndicator Component
 *
 * Shows performance indicators for a view configuration.
 * Helps users understand complexity before saving.
 */
export const PerformanceIndicator: React.FC<PerformanceIndicatorProps> = (props) => {
  const { chainDepth, joinCount, estimatedRowCount, hasViewJoins, showCompact = false } = props;
  const complexity = calculateComplexity(props);
  const colors = getComplexityColors(complexity);
  const percentage = getComplexityPercentage(complexity);
  const warningMessage = getWarningMessage(complexity, hasViewJoins);
  const icon = getComplexityIcon(complexity);

  if (showCompact) {
    return (
      <CompactPerformanceIndicator
        complexity={complexity}
        colors={colors}
        icon={icon}
        chainDepth={chainDepth}
        joinCount={joinCount}
        hasViewJoins={hasViewJoins}
      />
    );
  }

  return (
    <Card
      className={colors.bg}
      role="region"
      aria-label="View performance impact analysis"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          {icon}
          <span>Performance Impact</span>
          <Badge
            variant={colors.badge}
            className="ml-auto"
            aria-label={`Complexity level: ${complexity.replace('-', ' ')}`}
          >
            {complexity.toUpperCase().replace('-', ' ')}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Complexity Meter */}
        <div className="space-y-2" role="status" aria-label={`Query complexity: ${percentage} percent`}>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Query Complexity</span>
            <span aria-hidden="true">{percentage}%</span>
          </div>
          <Progress
            value={percentage}
            className="h-2"
            indicatorClassName={colors.progress}
            aria-label={`Complexity level ${complexity}`}
          />
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="space-y-1">
            <div className="text-muted-foreground text-xs">Dependency Depth</div>
            <div className="font-semibold flex items-center gap-1">
              {chainDepth}
              {chainDepth === 0 && (
                <span className="text-xs text-muted-foreground font-normal">(base)</span>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-muted-foreground text-xs">Total JOINs</div>
            <div className="font-semibold flex items-center gap-1">
              {joinCount}
              {hasViewJoins && (
                <Badge variant="outline" className="text-xs ml-1">
                  view
                </Badge>
              )}
            </div>
          </div>
          {estimatedRowCount !== undefined && (
            <div className="space-y-1 col-span-2">
              <div className="text-muted-foreground text-xs">Estimated Rows</div>
              <div className="font-semibold">
                {estimatedRowCount.toLocaleString()}
              </div>
            </div>
          )}
        </div>

        {/* Warning/Info Message */}
        <Alert variant={complexity === 'very-high' || complexity === 'high' ? 'destructive' : 'default'}>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {warningMessage}
          </AlertDescription>
        </Alert>

        {/* Recommendations */}
        {complexity === 'very-high' && (
          <div className="text-xs space-y-1 p-3 rounded-md bg-muted/50">
            <div className="font-semibold">Recommendations:</div>
            <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
              <li>Consider materializing intermediate views</li>
              <li>Break complex chains into smaller, reusable views</li>
              <li>Add indexes on frequently joined columns</li>
              <li>Schedule refreshes during off-peak hours</li>
            </ul>
          </div>
        )}
        {complexity === 'high' && (
          <div className="text-xs space-y-1 p-3 rounded-md bg-muted/50">
            <div className="font-semibold">Recommendations:</div>
            <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
              <li>Consider materializing this view if queried frequently</li>
              <li>Monitor query performance in production</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Compact version for inline display
 */
interface CompactPerformanceIndicatorProps {
  complexity: ComplexityLevel;
  colors: ReturnType<typeof getComplexityColors>;
  icon: React.ReactNode;
  chainDepth: number;
  joinCount: number;
  hasViewJoins: boolean;
}

const CompactPerformanceIndicator: React.FC<CompactPerformanceIndicatorProps> = ({
  complexity,
  colors,
  icon,
  chainDepth,
  joinCount,
  hasViewJoins,
}) => {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
      <div className={`p-2 rounded-md ${colors.bg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Performance Impact:</span>
          <Badge variant={colors.badge} className="text-xs">
            {complexity.toUpperCase().replace('-', ' ')}
          </Badge>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          <span>Depth: {chainDepth}</span>
          <span>•</span>
          <span>JOINs: {joinCount}</span>
          {hasViewJoins && (
            <>
              <span>•</span>
              <Badge variant="outline" className="text-xs">
                view JOINs
              </Badge>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
